// TGCLOUD-1669 Phase 2 — the tenant field-label map, in one place.
//
// Three person fields are relabellable per tenant by an administrator: mobile,
// hospital number and the healthcare identifier (NHS Number here, Medicare
// Number in Australia, CPR in Denmark). The backend stores a global singleton
// on `securitysettings` overlaid by a per-userGroup row on `grouppreferences`,
// and GET /settings/pedigree/field-labels resolves the two.
//
// Before this hook there was no shared reader: `pedigreeFieldLabels` appeared
// in exactly three places in the repo, two of which were inside PersonMenu,
// which fetched the map into its own useState. Anything else that wanted to
// print those labels hard-coded the English string, and at least eleven files
// do (formatPersonSummary, the pedigree node labels, BulkEditModal, the merge
// wizard). The pinned person header would have been the twelfth, and it sits
// directly above the form, so the two would visibly disagree the moment an
// admin renamed a field.
//
// React Query rather than a context: the query key is shared, so whichever
// consumer asks first pays for the request and the rest of the session is a
// cache hit. That is the same idiom `familySearchIndex` already uses in
// PersonMenu, and it means adding a consumer costs no extra request.

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

/** English defaults, used until the tenant map resolves and for any key it omits. */
export const DEFAULT_PEDIGREE_FIELD_LABELS = Object.freeze({
  mobile: 'Mobile',
  hospitalNumber: 'Hospital Number',
  healthcareId: 'Healthcare ID'
});

export const PEDIGREE_FIELD_LABELS_KEY = ['pedigreeFieldLabels'];

/** The three keys the backend accepts. Anything else on the response is ignored. */
export const RELABELLABLE_FIELDS = Object.freeze(
  Object.keys(DEFAULT_PEDIGREE_FIELD_LABELS)
);

/**
 * Merge a tenant response over the defaults.
 *
 * Only a non-empty string overrides. The backend stores an unset label as
 * `null` (PUT maps an empty string to null), and a `Mixed` column can hold
 * anything, so a blank must fall back to the default rather than rendering a
 * field with no label at all.
 *
 * Pure and exported so the merge rule is testable without a QueryClient.
 */
export const mergeFieldLabels = (raw) => {
  const merged = { ...DEFAULT_PEDIGREE_FIELD_LABELS };
  if (!raw || typeof raw !== 'object') return merged;
  RELABELLABLE_FIELDS.forEach((key) => {
    const value = raw[key];
    if (typeof value === 'string' && value.trim()) merged[key] = value.trim();
  });
  return merged;
};

/**
 * The resolved label map. Always returns every key, so a caller can print
 * `labels.hospitalNumber` without a fallback of its own.
 *
 * @returns {{ mobile: string, hospitalNumber: string, healthcareId: string }}
 */
export default function usePedigreeFieldLabels() {
  const { data } = useQuery({
    queryKey: PEDIGREE_FIELD_LABELS_KEY,
    staleTime: 5 * 60 * 1000,
    // A tenant that has never renamed anything is the common case and the
    // defaults are correct for it, so a failed fetch is not worth retrying or
    // surfacing: the map simply stays English.
    retry: false,
    queryFn: async () => {
      const res = await axios.get(APICONSTANTS.pedigreeFieldLabels);
      return res.data || {};
    }
  });

  return useMemo(() => mergeFieldLabels(data), [data]);
}

/**
 * Save one relabelled field and refresh every consumer.
 *
 * Invalidating the shared key is the point: the person header, the PERSON form
 * grid and anything else reading the map all re-render with the new label,
 * rather than only the component that happened to own the save.
 *
 * @returns {(field: string, label: string) => Promise<void>}
 */
export function useSavePedigreeFieldLabel() {
  const queryClient = useQueryClient();
  return useMemo(
    () => async (field, label) => {
      const trimmed = String(label ?? '').trim();
      if (!trimmed || !RELABELLABLE_FIELDS.includes(field)) {
        throw new Error(`Not a relabellable field: ${field}`);
      }
      await axios.put(APICONSTANTS.pedigreeFieldLabels, { [field]: trimmed });
      await queryClient.invalidateQueries({
        queryKey: PEDIGREE_FIELD_LABELS_KEY
      });
    },
    [queryClient]
  );
}
